import React from "react"

import EcosuiteModule from "@common/module/EcosuiteModule"
import DateRangeUtils from "@common/utils/DateRangeUtils"

import EnergyService from "./EnergyService"
import UserAlertService from "../event/UserAlertService"

import { EcosuiteComponentError, Loading } from "@common/EcosuiteComponent"
import Aggregations from "@common/Aggregations"
import "./EnergyModule.css"
import { EnergyProjectViews, EnergyModuleViews } from "./EnergyViews"
import Logger from "@common/Logger"
import moment from "moment"

export default class EnergyModule extends EcosuiteModule {
  constructor(props) {
    super(props, "energy")

    this.state.dateRangeIncludesTime = true
  }

  componentDidMount() {
    super.componentDidMount()

    this.load()

    let interval = setInterval(() => {
      this.loadInstantaneous()
      this.loadEnergyStatus()
    }, 300000) // We update the "live" data every regularly, 300000 = 5 minutes
    this.setStateIfMounted({ interval: interval })
  }

  componentWillUnmount() {
    clearInterval(this.state.interval)
  }

  componentDidUpdate(prevProps) {
    super.componentDidUpdate(prevProps)

    // We recalculate the date range for "All Time" so that the graphs can be updated if necessary
    this.recalculateAllTimeRange(prevProps)

    if (this.props.loadTime !== prevProps.loadTime) {
      // Load all the module data
      this.load()
    } else if (
      JSON.stringify(this.props.filteredProjects.map((p) => p.code)) !==
      JSON.stringify(prevProps.filteredProjects.map((p) => p.code))
    ) {
      // Portfolio has been changed or the projects filtered, reload
      Logger.debug(`load datums for portfolio projects: ${this.props.filteredProjects.map((p) => p.code)}`)
      this.loadProjects()
    } else if (!this.props.portfolio && this.props.project !== prevProps.project) {
      // When a portfolio is selected, we just use the loaded datums loaded for the portfolios projects; if not we need to load the details for the selected project
      Logger.debug(`load Project datums`)
      this.loadProjects()
    }
  }

  selectRange(rangeName, customRange) {
    let range = super.selectRange(rangeName, customRange)
    this.setStateIfMounted({ aggregation: undefined }, () => {
      this.loadRange(range)
    })
  }

  /**
   * Loads all the data required by the module.
   */
  load() {
    this.loadRange(this.getExclusiveRange())

    this.loadLastMonthsEnergyReadings()
    this.loadTodaysEnergyDatums()
    this.loadInstantaneous()
    this.loadEnergyStatus()
  }

  /**
   * Loads the data required when the filtered projects changes; when the portfolio, filter or project selection changes, it means that project specific data should be loaded
   */
  loadProjects() {
    /*
     * EP-1850 We try to not load the data for all projects at once as this is no longer performant, this is a WIP.
     *
     * EP-2167: we currently load project data for all the currently filtered projects, ie for all those in the current "portfolio"; when a portfolio isn't selected we only load a single project.
     * This is because when we load a portfolio, we then pass the project data into the project view so that it can be viewed instantly without needing to load additional data.
     *
     * Currently we're NOT taking the selected projects into account, ie we load the filtered projects (all projects in the "portfolio") and are NOT taking the selections into account;
     * this means that the totals currently shown are for the entire portfolio, not just the currently selected projects in the portfolio.
     * As this is how the totals have always worked (ie not taking selections into account) this is fine for now; in the future we may want to take selections into account.
     * When we do we can either make an additional call to get the data for the selected projects as well, which will mean that the server calculates the total or,
     * do the filtering client side from the filtered projects, only totalling the filtered projects that we know to be selected (as selected is a subset of filtered).
     *
     * */

    // We only reload the range data that is project specific
    const range = this.getExclusiveRange()
    this.loadRange(range)
    this.loadEnergyDatums(range)
    this.loadEnergyReadings(range)

    this.loadLastMonthsEnergyReadings()
    this.loadTodaysEnergyDatums()
    this.loadInstantaneous()
    this.loadEnergyStatus()
  }

  /**
   * Load all the data dependent on the selected range.
   */
  loadRange(range) {
    this.loadBatteryDatums(range)
    this.loadEnergyDatums(range)
    this.loadPredictedConsumption(range)
    this.loadConsumptionCost(range)
    this.loadExpectedGeneration(range)
    this.loadPredictedGeneration(range)
    this.loadEnergyReadings(range)
    this.loadUserAlerts(range)

    const aggregate = this.getAggregateForRange(range)

    this.loadConsumptionDatums(range, aggregate)
    this.loadPreviousEnergyReadings(range, aggregate)
  }

  getAggregateForRange(range) {
    let aggregate = DateRangeUtils.getAggregateForRange(range)
    if (range.diff("days", true) <= 1) {
      aggregate = Aggregations.FifteenMinute
    }
    return aggregate
  }

  async loadBatteryDatums(range) {
    if (!this.props.project) {
      return
    }
    this.setState({ loading: true })
    const aggregate = this.getAggregateForRange(range)
    const projectId = this.props.project.code
    const loadingSourceIds = [`**/BESS/*`, `**/STO/*`]
    const batteryDatums = await EnergyService.getNodesDatums(range, aggregate, projectId, null, loadingSourceIds)
      .then((response) => {
        if (response.data.error) {
          Logger.error("Error response for nodes datums for " + projectId + ": " + response.data.error)
        } else {
          return response
        }
      })
      .catch((err) => {
        Logger.error("failed to get nodes datums for " + projectId + ": " + err)
      })

    this.setState({ batteryDatums: batteryDatums })
  }

  loadConsumptionDatums(range, aggregate) {
    // Clear the existing state to make it clear an update is occuring
    this.setStateIfMounted(
      {
        econodeDatums: undefined,
        econodeDatumsError: undefined,
      },
      () => {
        if (this.props.project) {
          EnergyService.getProjectConsumptionDatums(range, aggregate, this.props.project.code)
            .then((response) => {
              if (this.isRangeCurrent(range)) {
                this.setStateIfMounted({
                  econodeDatums: response,
                })
              } else {
                Logger.debug(`Ignoring out of date response for range: ${range}`)
              }
            })
            .catch((err) => {
              Logger.error(err)
              if (this.isRangeCurrent(range)) {
                this.setStateIfMounted({
                  econodeDatums: new EcosuiteComponentError(err),
                })
              }
            })
        }
      },
    )
  }

  loadPreviousEnergyReadings(range, aggregate) {
    // Clear the existing state to make it clear an update is occuring
    this.setStateIfMounted({
      econodeActualRange: undefined,
      econodePreviousRange: undefined,
      econodePreviousReadings: undefined,
    })

    const now = moment()
    if (range.start.isAfter(now)) {
      // We set to null to indicate that there is no actual/previous data to load for the selected dates
      this.setStateIfMounted({
        econodeActualRange: null,
        econodePreviousRange: null,
        econodePreviousReadings: null,
      })
    } else {
      let actualRange = moment.range(
        range.start,
        range.end.isAfter(now) ? now.add(1, aggregate).startOf(aggregate) : range.end,
      )
      let rangeLength = actualRange.end - actualRange.start
      let previousRange = moment.range(actualRange.start - rangeLength, actualRange.end - rangeLength)

      EnergyService.getEnergyReadings(previousRange)
        .then((response) => {
          if (this.isRangeCurrent(range)) {
            this.setStateIfMounted({
              econodeActualRange: actualRange,
              econodePreviousRange: previousRange,
              econodePreviousReadings: response,
            })
          } else {
            Logger.debug(`Ignoring out of date response for range: ${range}`)
          }
        })
        .catch((err) => {
          Logger.error(err)
          if (this.isRangeCurrent(range)) {
            this.setStateIfMounted({
              econodeActualRange: actualRange,
              econodePreviousRange: previousRange,
              econodePreviousReadings: new EcosuiteComponentError(err),
            })
          }
        })
    }
  }

  loadUserAlerts(range) {
    // Clear the existing state to make it clear an update is occuring
    this.setStateIfMounted(
      {
        userAlerts: undefined,
      },
      () => {
        UserAlertService.getUserAlerts(this.props.userId, range)
          .then((alerts) => {
            if (this.isRangeCurrent(range)) {
              this.setStateIfMounted({
                userAlerts: alerts,
              })
            } else {
              Logger.debug(`Ignoring out of date response for range: ${range}`)
            }
          })
          .catch((err) => {
            Logger.error(err)
            if (this.isRangeCurrent(range)) {
              this.setStateIfMounted({
                userAlerts: new EcosuiteComponentError(err),
              })
            }
          })
      },
    )
  }

  /**
   *
   * @returns {[string]} the filtered project codes
   */
  getFilteredProjectIds() {
    return (this.props.project && !this.props.portfolio ? [this.props.project] : this.props.filteredProjects).map(
      (p) => p.code,
    )
  }

  areFilteredProjectIdsCurrent(filteredProjectIds) {
    const latestProjectIds = this.getFilteredProjectIds()
    return JSON.stringify(filteredProjectIds) === JSON.stringify(latestProjectIds)
  }

  loadEnergyDatums(range) {
    // Use per-site loading for single project view
    if (this.props.project && !this.props.portfolio) {
      this.loadEnergyDatumsPerSite(range, this.props.project)
      return
    }

    // Clear the existing state to make it clear an update is occuring
    this.setStateIfMounted(
      {
        datums: undefined,
      },
      () => {
        const projectIds = this.getFilteredProjectIds()
        Logger.debug(`load datums for projects: ${projectIds}`)
        EnergyService.getEnergyDatums(range, projectIds)
          .then((response) => {
            if (this.isRangeCurrent(range) && this.areFilteredProjectIdsCurrent(projectIds)) {
              this.setStateIfMounted({
                datums: response,
              })
            } else {
              Logger.debug(`Ignoring out of date response for range: ${range} and projects: ${projectIds}`)
            }
          })
          .catch((err) => {
            Logger.error(err)
            if (this.isRangeCurrent(range) && this.areFilteredProjectIdsCurrent(projectIds)) {
              this.setStateIfMounted({
                datums: new EcosuiteComponentError(err),
              })
            }
          })
      },
    )
  }

  loadEnergyDatumsPerSite(range, project) {
    const projectCode = project.code
    const sites = Object.values(project.sites)
    const aggregation = this.getAggregateForRange(range)

    // Initialize with null for each site (will be populated as data loads)
    const initialSiteStates = {}
    sites.forEach((site) => {
      initialSiteStates[site.code] = null
    })

    this.setStateIfMounted({
      datums: {
        projects: {
          [projectCode]: {
            sites: initialSiteStates,
          },
        },
      },
    })

    // Fire requests for each site, giving the first site a 500ms head start
    sites.forEach((site, index) => {
      const delay = index === 0 ? 0 : 500

      setTimeout(() => {
        EnergyService.getSiteDatums(range, projectCode, site.code, aggregation)
          .then((response) => {
            if (this.isRangeCurrent(range) && this.isProjectCurrent(projectCode)) {
              // API returns { site: { ... }, range: { ... }, aggregation: { ... } }
              this.updateSiteDatums(projectCode, site.code, response.site, response.range, response.aggregation)
            }
          })
          .catch((err) => {
            Logger.error(`Failed to load datums for site ${site.code}: ${err}`)
            if (this.isRangeCurrent(range) && this.isProjectCurrent(projectCode)) {
              this.updateSiteDatums(projectCode, site.code, { __error: err }, null, null)
            }
          })
      }, delay)
    })
  }

  updateSiteDatums(projectCode, siteCode, data, range, aggregation) {
    this.setStateIfMounted((prevState) => {
      const currentDatums = prevState.datums || { projects: {} }
      const currentProject = currentDatums.projects[projectCode] || { sites: {} }

      // Store site data directly to match original structure
      const updatedSites = {
        ...currentProject.sites,
        [siteCode]: data,
      }

      // Aggregate sources from all loaded sites for project-level access
      const aggregatedSources = {}
      Object.values(updatedSites).forEach((siteData) => {
        if (siteData?.sources && !siteData.__error) {
          Object.assign(aggregatedSources, siteData.sources)
        }
      })

      return {
        datums: {
          ...currentDatums,
          // Preserve range and aggregation at top level to match bulk loading structure
          range: range || currentDatums.range,
          aggregation: aggregation || currentDatums.aggregation,
          projects: {
            ...currentDatums.projects,
            [projectCode]: {
              // Aggregated project-level sources
              sources: aggregatedSources,
              // Per-site data
              sites: updatedSites,
            },
          },
        },
      }
    })
  }

  isProjectCurrent(projectCode) {
    return this.props.project?.code === projectCode
  }

  loadTodaysEnergyDatums() {
    // Clear the existing state to make it clear an update is occuring
    this.setStateIfMounted({
      todaysEnergyDatums: undefined,
    })

    const projectIds = this.getFilteredProjectIds()
    EnergyService.getEnergyDatums(DateRangeUtils.getRange("today"), projectIds)
      .then((response) => {
        if (this.areFilteredProjectIdsCurrent(projectIds)) {
          this.setStateIfMounted({
            todaysEnergyDatums: response,
          })
        }
      })
      .catch((err) => {
        if (this.areFilteredProjectIdsCurrent(projectIds)) {
          this.setStateIfMounted({
            todaysEnergyDatums: new EcosuiteComponentError(err),
          })
        }
      })
  }

  loadPredictedConsumption(range) {
    // Clear the existing state to make it clear an update is occuring
    this.setStateIfMounted({
      predictedConsumption: undefined,
    })

    EnergyService.getForecastConsumption(range)
      .then((response) => {
        if (this.isRangeCurrent(range)) {
          this.setStateIfMounted({
            predictedConsumption: response,
          })
        } else {
          Logger.debug(`Ignoring out of date response for range: ${range}`)
        }
      })
      .catch((err) => {
        Logger.error(err)
        if (this.isRangeCurrent(range)) {
          this.setStateIfMounted({
            predictedConsumption: new EcosuiteComponentError(err),
          })
        }
      })
  }

  loadConsumptionCost(range) {
    // Clear the existing state to make it clear an update is occuring
    this.setStateIfMounted({
      consumptionCost: undefined,
    })

    EnergyService.getConsumptionCost(range)
      .then((response) => {
        if (this.isRangeCurrent(range)) {
          this.setStateIfMounted({
            consumptionCost: response,
          })
        } else {
          Logger.debug(`Ignoring out of date response for range: ${range}`)
        }
      })
      .catch((err) => {
        Logger.error(err)
        if (this.isRangeCurrent(range)) {
          this.setStateIfMounted({
            consumptionCost: new EcosuiteComponentError(err),
          })
        }
      })
  }

  loadExpectedGeneration(range) {
    // Clear the existing state to make it clear an update is occuring
    this.setStateIfMounted({
      expectedGeneration: undefined,
    })

    EnergyService.getExpectedGeneration(range, null, this.getFilteredProjectIds())
      .then((response) => {
        if (this.isRangeCurrent(range)) {
          this.setStateIfMounted({
            expectedGeneration: response,
          })
        } else {
          Logger.debug(`Ignoring out of date response for range: ${range}`)
        }
      })
      .catch((err) => {
        Logger.error(err)
        if (this.isRangeCurrent(range)) {
          this.setStateIfMounted({
            expectedGeneration: new EcosuiteComponentError(err),
          })
        }
      })
  }

  loadPredictedGeneration(range) {
    // Clear the existing state to make it clear an update is occuring
    this.setStateIfMounted({
      predictedGeneration: undefined,
    })

    const projectIds = this.getFilteredProjectIds()
    EnergyService.getPredictedGeneration(range, projectIds)
      .then((response) => {
        if (this.isRangeCurrent(range)) {
          this.setStateIfMounted({
            predictedGeneration: response,
          })
        } else {
          Logger.debug(`Ignoring out of date response for range: ${range}`)
        }
      })
      .catch((err) => {
        Logger.error(err)
        if (this.isRangeCurrent(range)) {
          this.setStateIfMounted({
            predictedGeneration: new EcosuiteComponentError(err),
          })
        }
      })
  }

  loadEnergyReadings(range) {
    // Handle "lastMonth" special case first, before per-site loading
    if (range === "lastMonth") {
      // Use per-site loading for single project view
      if (this.props.project && !this.props.portfolio) {
        if (this.isRangeCurrent(range)) {
          this.loadEnergyReadingsPerSite(this.state.lastMonthsEnergyReadings?.range || range, this.props.project)
        }
        return
      }
    } else {
      // Use per-site loading for single project view (non-lastMonth ranges)
      if (this.props.project && !this.props.portfolio) {
        this.loadEnergyReadingsPerSite(range, this.props.project)
        return
      }
    }

    // Clear the existing state to make it clear an update is occuring
    this.setStateIfMounted({
      readings: undefined,
    })

    if (range === "lastMonth") {
      // The load for 'lastMonth' is handled by loadLastMonthsEnergyReadings()
      if (this.isRangeCurrent(range)) {
        this.setStateIfMounted({
          readings: this.state.lastMonthsEnergyReadings,
        })
      } else {
        Logger.debug(`Ignoring out of date response for range: ${range}`)
      }
    } else {
      const projectIds = this.getFilteredProjectIds()
      EnergyService.getEnergyReadings(range, projectIds)
        .then((response) => {
          if (this.isRangeCurrent(range) && this.areFilteredProjectIdsCurrent(projectIds)) {
            this.setStateIfMounted({
              readings: response,
            })
          } else {
            Logger.debug(`Ignoring out of date response for range: ${range}`)
          }
        })
        .catch((err) => {
          Logger.error(err)
          if (this.isRangeCurrent(range) && this.areFilteredProjectIdsCurrent(projectIds)) {
            this.setStateIfMounted({
              readings: new EcosuiteComponentError(err),
            })
          }
        })
    }
  }

  loadEnergyReadingsPerSite(range, project) {
    const projectCode = project.code
    const sites = Object.values(project.sites)

    // Initialize with null for each site (will be populated as data loads)
    const initialSiteStates = {}
    sites.forEach((site) => {
      initialSiteStates[site.code] = null
    })

    this.setStateIfMounted({
      readings: {
        projects: {
          [projectCode]: {
            sites: initialSiteStates,
          },
        },
      },
    })

    // Fire requests for each site, giving the first site a 500ms head start
    sites.forEach((site, index) => {
      const delay = index === 0 ? 0 : 500

      setTimeout(() => {
        EnergyService.getSiteReadings(range, projectCode, site.code)
          .then((response) => {
            if (this.isRangeCurrent(range) && this.isProjectCurrent(projectCode)) {
              // API returns { site: { ... }, range: { ... } } - extract the site data
              this.updateSiteReadings(projectCode, site.code, response.site)
            }
          })
          .catch((err) => {
            Logger.error(`Failed to load readings for site ${site.code}: ${err}`)
            if (this.isRangeCurrent(range) && this.isProjectCurrent(projectCode)) {
              this.updateSiteReadings(projectCode, site.code, { __error: err })
            }
          })
      }, delay)
    })
  }

  updateSiteReadings(projectCode, siteCode, data) {
    this.setStateIfMounted((prevState) => {
      const currentReadings = prevState.readings || { projects: {} }
      const currentProject = currentReadings.projects[projectCode] || { sites: {} }

      // Store site data directly to match original structure
      const updatedSites = {
        ...currentProject.sites,
        [siteCode]: data,
      }

      // Calculate aggregated totals from all loaded sites
      let generation = 0
      let consumption = 0
      let storage = 0
      let emissions = 0
      let avoidedEmissions = 0
      let exportValue = 0

      Object.values(updatedSites).forEach((siteData) => {
        if (siteData && !siteData.__error) {
          generation += siteData.generation || 0
          consumption += siteData.consumption || 0
          storage += siteData.storage || 0
          emissions += siteData.emissions || 0
          avoidedEmissions += siteData.avoidedEmissions || 0
          exportValue += siteData.export || 0
        }
      })

      return {
        readings: {
          ...currentReadings,
          projects: {
            ...currentReadings.projects,
            [projectCode]: {
              // Aggregated project-level values
              generation,
              consumption,
              storage,
              emissions,
              avoidedEmissions,
              export: exportValue,
              // Per-site data
              sites: updatedSites,
            },
          },
        },
      }
    })
  }

  loadLastMonthsEnergyReadings() {
    // Clear the existing state to make it clear an update is occuring
    this.setStateIfMounted({
      lastMonthsEnergyReadings: undefined,
    })

    const projectIds = this.getFilteredProjectIds()
    EnergyService.getEnergyReadings(DateRangeUtils.getRange("lastMonth"), projectIds)
      .then((response) => {
        if (this.areFilteredProjectIdsCurrent(projectIds)) {
          this.setStateIfMounted({
            lastMonthsEnergyReadings: response,
          })
          if (this.state.rangeName === "lastMonth" && !(this.props.project && !this.props.portfolio)) {
            // If the view is for the last month we also populate the energy here
            // Skip this for single project view to avoid overwriting per-site progressive loading
            this.setStateIfMounted({
              readings: response,
            })
          }
        }
      })
      .catch((err) => {
        Logger.error(err)
        if (this.areFilteredProjectIdsCurrent(projectIds)) {
          this.setStateIfMounted({
            lastMonthsEnergyReadings: new EcosuiteComponentError(err),
          })
        }
      })
  }

  loadInstantaneous() {
    // Clear the existing state to make it clear an update is occuring
    this.setStateIfMounted({
      instantaneous: undefined,
    })

    const projectIds = this.getFilteredProjectIds()
    EnergyService.getInstantaneous(projectIds)
      .then((response) => {
        if (this.areFilteredProjectIdsCurrent(projectIds)) {
          this.setStateIfMounted({
            instantaneous: response,
          })
        }
      })
      .catch((err) => {
        if (this.areFilteredProjectIdsCurrent(projectIds)) {
          this.setStateIfMounted({
            instantaneous: new EcosuiteComponentError(err),
          })
        }
      })
  }

  loadEnergyStatus() {
    // Clear the existing state to make it clear an update is occuring
    this.setStateIfMounted({
      projectsStatus: undefined,
    })

    const projectIds = this.getFilteredProjectIds()
    EnergyService.getStatus(projectIds)
      .then((response) => {
        if (this.areFilteredProjectIdsCurrent(projectIds)) {
          this.setStateIfMounted({
            projectsStatus: response,
          })
        }
      })
      .catch((err) => {
        if (this.areFilteredProjectIdsCurrent(projectIds)) {
          this.setStateIfMounted({
            projectsStatus: new EcosuiteComponentError(err),
          })
        }
      })
  }

  getProjectStatus(project) {
    if (this.isContentValid(this.state.projectsStatus)) {
      return this.state.projectsStatus.projects.find((projectStatus) => {
        return projectStatus.code === project.code
      })
    }
    return this.state.projectsStatus
  }

  // Helper methods to distinguish between loading and error states for sites
  getSiteDataState(projectCode, siteCode, dataType = "readings") {
    const projectData = this.state[dataType]?.projects?.[projectCode]
    if (!projectData) {
      return { loading: true, error: null, data: null }
    }

    const siteData = projectData.sites?.[siteCode]
    if (siteData === null) {
      return { loading: true, error: null, data: null }
    }

    if (siteData?.__error) {
      return { loading: false, error: siteData.__error, data: null }
    }

    return { loading: false, error: null, data: siteData }
  }

  getSiteReadingsState(projectCode, siteCode) {
    return this.getSiteDataState(projectCode, siteCode, "readings")
  }

  getSiteDatumsState(projectCode, siteCode) {
    return this.getSiteDataState(projectCode, siteCode, "datums")
  }

  renderProjectView() {
    let project = this.props.project
    return (
      <div className="EnergyView">
        <EnergyProjectViews
          restrictions={this.props.restrictions}
          groups={this.props.groups}
          view={this.state.projectView}
          range={this.getExclusiveRange()}
          rangeName={this.state.rangeName}
          customRange={this.getCustomRange()}
          selectRange={this.selectRange}
          project={project}
          projects={[project]}
          projectStatus={this.getProjectStatus(project)}
          datums={
            this.isContentValid(this.state.datums?.projects)
              ? this.state.datums.projects[project.code]
              : this.state.datums
          }
          datumsRange={this.isContentValid(this.state.datums?.range) ? this.state.datums.range : this.state.datums}
          datumsAggregation={
            this.isContentValid(this.state.datums?.aggregation) ? this.state.datums.aggregation : this.state.datums
          }
          batteryDatums={this.state.batteryDatums}
          consumptionCost={
            this.isContentValid(this.state.consumptionCost)
              ? this.state.consumptionCost.projects[project.code]
              : this.state.consumptionCost
          }
          predictedConsumption={
            this.isContentValid(this.state.predictedConsumption)
              ? this.state.predictedConsumption.projects[project.code]
              : this.state.predictedConsumption
          }
          expectedGeneration={
            this.isContentValid(this.state.expectedGeneration)
              ? this.state.expectedGeneration.projects[project.code]
              : this.state.expectedGeneration
          }
          predictedGeneration={
            this.isContentValid(this.state.predictedGeneration)
              ? this.state.predictedGeneration.projects[project.code]
              : this.state.predictedGeneration
          }
          instantaneous={
            this.isContentValid(this.state.instantaneous)
              ? this.state.instantaneous.projects[project.code]
              : this.state.instantaneous
          }
          instantaneousTimestamp={
            this.isContentValid(this.state.instantaneous)
              ? this.state.instantaneous.timestamp
              : this.state.instantaneous
          }
          readings={
            this.isContentValid(this.state.readings) ? this.state.readings.projects[project.code] : this.state.readings
          }
          lastMonthsEnergyReadings={
            this.isContentValid(this.state.lastMonthsEnergyReadings)
              ? this.state.lastMonthsEnergyReadings.projects[project.code]
              : this.state.lastMonthsEnergyReadings
          }
          userAlerts={this.state.userAlerts}
          showGeneration={this.isGenerationVisible()}
          showConsumption={this.isConsumptionVisible()}
          showStorage={this.isStorageVisible()}
          econodeActualRange={this.state.econodeActualRange}
          econodePreviousRange={this.state.econodePreviousRange}
          econodePreviousReadings={
            this.state.econodePreviousReadings?.projects
              ? this.state.econodePreviousReadings.projects[project.code]
              : null
          }
          econodeDatums={this.state.econodeDatums}
          userId={this.props.userId}
        />
      </div>
    )
  }

  renderModuleView() {
    if (this.isContentValid(this.state.readings)) {
      // EP-2167 we have a check to ensure that the readings are loaded for the current projects
      const projectIds = this.props.filteredProjects.map((p) => p.code).sort()
      const readingProjectIds = Object.keys(this.state.readings.projects).sort()
      if (JSON.stringify(projectIds) !== JSON.stringify(readingProjectIds)) {
        Logger.debug(`Current projects: ${projectIds}`)
        Logger.debug(`Ignoring out of date readings for projects: ${readingProjectIds}`)
        return <Loading />
      }
    }

    return (
      <div className="EnergyView">
        <EnergyModuleViews
          restrictions={this.props.restrictions}
          groups={this.props.groups}
          view={this.state.moduleView}
          range={this.getExclusiveRange()}
          rangeName={this.state.rangeName}
          customRange={this.getCustomRange()}
          selectProject={this.selectProject}
          selectRange={this.selectRange}
          projects={this.props.projects}
          instantaneous={this.state.instantaneous}
          recentDatums={this.state.todaysEnergyDatums}
          projectsStatus={this.state.projectsStatus}
          readings={this.state.readings}
          lastMonthsEnergyReadings={this.state.lastMonthsEnergyReadings}
          datums={this.state.datums}
          consumptionCost={this.state.consumptionCost}
          predictedConsumption={this.state.predictedConsumption}
          expectedGeneration={this.state.expectedGeneration}
          predictedGeneration={this.state.predictedGeneration}
          userAlerts={this.state.userAlerts}
          showGeneration={this.isGenerationVisible()}
          showConsumption={this.isConsumptionVisible()}
          showStorage={this.isStorageVisible()}
          userId={this.props.userId}
        />
      </div>
    )
  }
}
